import { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  ArrowLeft,
  ArrowRight,
  Anchor,
  Check,
  Trash2,
  Plus,
  Minus,
  CalendarDays,
  Clock,
  Users,
  Sparkles,
  User,
  Copy,
} from "lucide-react";
import { EXPERIENCES, TIME_SLOTS, ADD_ONS, PICKUP_DOCKS } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: "Experience", icon: Anchor },
  { id: 2, label: "Date & Time", icon: CalendarDays },
  { id: 3, label: "Add-ons", icon: Sparkles },
  { id: 4, label: "Details", icon: User },
];

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function BookNow() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const initialExp = state?.experience || EXPERIENCES[0].name;

  const [step, setStep] = useState(1);
  const [experience, setExperience] = useState(initialExp);
  const [guests, setGuests] = useState(4);
  const [children, setChildren] = useState(0);
  const [dates, setDates] = useState([]); // { date: Date, start_time, duration_hours, overnight }
  const [currentDate, setCurrentDate] = useState(null);
  const [startTime, setStartTime] = useState("10:00");
  const [durationHours, setDurationHours] = useState(3);
  const [addOns, setAddOns] = useState([]);
  const [addOnQty, setAddOnQty] = useState({}); // {id: qty}
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", notes: "", pickup_dock: "" });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState(null); // { code, balance, ... }
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [wireSent, setWireSent] = useState(false);
  const [wireSentSubmitting, setWireSentSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bank-details`).then((r) => setBankDetails(r.data)).catch(() => {});
  }, []);

  // Group booking state
  const [groupEnabled, setGroupEnabled] = useState(false);
  const [splitMode, setSplitMode] = useState("equal");
  const [partySize, setPartySize] = useState(4);
  const [customShares, setCustomShares] = useState([
    { name: "", email: "", amount: 0 },
    { name: "", email: "", amount: 0 },
  ]);

  async function applyVoucher() {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    setVoucherChecking(true);
    try {
      const res = await axios.post(`${API}/gifts/validate`, { code });
      setVoucher(res.data);
      toast.success(`Voucher applied: $${res.data.balance.toLocaleString()} balance`);
    } catch (e) {
      setVoucher(null);
      toast.error(e?.response?.data?.detail || "Invalid voucher.");
    } finally {
      setVoucherChecking(false);
    }
  }

  function clearVoucher() {
    setVoucher(null);
    setVoucherCode("");
  }

  const isOvernight = experience === "Overnight Stays";
  const isBusTour = experience === "Mini Bus Tour";
  const maxGuests = isBusTour ? 20 : 10;
  const minDurationHours = isBusTour ? 4 : 1;

  // Enforce minimum duration when experience switches (e.g. Mini Bus → 4hr min)
  useEffect(() => {
    if (isBusTour && durationHours < 4) setDurationHours(4);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience]);

  // Recompute quote when inputs change
  useEffect(() => {
    if (dates.length === 0) {
      setQuote(null);
      return;
    }
    const payload = {
      experience,
      guests,
      children,
      dates: dates.map((d) => ({
        date: toISO(d.date),
        start_time: d.start_time,
        duration_hours: d.duration_hours,
        overnight: d.overnight,
      })),
      add_ons: addOns,
        add_on_quantities: addOnQty,
      full_name: form.full_name || "preview",
      email: form.email || "preview@example.com",
      phone: form.phone || "000",
      notes: form.notes,
      voucher_code: voucher?.code || null,
    };
    let cancelled = false;
    axios
      .post(`${API}/bookings/quote`, payload)
      .then((r) => !cancelled && setQuote(r.data))
      .catch(() => !cancelled && setQuote(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experience, guests, children, dates, addOns, addOnQty, voucher]);

  const canNext = useMemo(() => {
    if (step === 1) return !!experience && guests >= 1 && guests <= 10;
    if (step === 2) return dates.length > 0;
    if (step === 3) return true;
    if (step === 4) return form.full_name && form.email && form.phone;
    return false;
  }, [step, experience, guests, dates, form]);

  function addDate() {
    if (!currentDate) {
      toast.error("Please pick a date on the calendar first.");
      return;
    }
    const iso = toISO(currentDate);
    if (dates.find((d) => toISO(d.date) === iso)) {
      toast.error("That date is already added.");
      return;
    }
    setDates((prev) => [
      ...prev,
      {
        date: currentDate,
        start_time: isOvernight ? "17:00" : startTime,
        duration_hours: isOvernight ? 0 : durationHours,
        overnight: isOvernight,
      },
    ]);
    setCurrentDate(null);
    toast.success("Date added to your charter.");
  }

  function removeDate(iso) {
    setDates((prev) => prev.filter((d) => toISO(d.date) !== iso));
  }

  function toggleAddon(id) {
    setAddOns((prev) => {
      const has = prev.includes(id);
      if (has) {
        // remove + drop its quantity
        setAddOnQty((q) => {
          const next = { ...q };
          delete next[id];
          return next;
        });
        return prev.filter((a) => a !== id);
      }
      // add — default qty: total guests for per-guest items, 1 for flat
      const addon = ADD_ONS.find((x) => x.id === id);
      const defaultQty = addon?.unit === "flat" ? 1 : Math.max(1, guests);
      setAddOnQty((q) => ({ ...q, [id]: defaultQty }));
      return [...prev, id];
    });
  }

  function changeAddonQty(id, delta) {
    setAddOnQty((q) => {
      const current = q[id] || 1;
      const next = Math.max(1, Math.min(99, current + delta));
      return { ...q, [id]: next };
    });
  }

  async function submit() {
    setSubmitting(true);
    try {
      const payload = {
        experience,
        guests,
        children,
        dates: dates.map((d) => ({
          date: toISO(d.date),
          start_time: d.start_time,
          duration_hours: d.duration_hours,
          overnight: d.overnight,
        })),
        add_ons: addOns,
        add_on_quantities: addOnQty,
        pickup_dock: form.pickup_dock || "",
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        voucher_code: voucher?.code || null,
        group: groupEnabled
          ? {
              enabled: true,
              split_mode: splitMode,
              party_size: splitMode === "equal" ? partySize : customShares.length,
              shares: splitMode === "custom" ? customShares.map((s) => ({
                name: s.name || null,
                email: s.email || null,
                amount: Number(s.amount) || 0,
              })) : [],
            }
          : null,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setConfirmed(res.data);
      toast.success("Booking request submitted. We'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Confirmation state
  if (confirmed) {
    const isGroup = !!confirmed.is_group_booking;
    const shareUrl = isGroup ? `${window.location.origin}/share/${confirmed.id}` : null;
    return (
      <div data-testid="booking-confirmed" className="bg-sand min-h-[80vh]">
        <section className="max-w-[900px] mx-auto px-6 lg:px-12 pt-28 pb-20 text-center">
          <div className="mx-auto w-14 h-14 bg-navy text-white flex items-center justify-center mb-8">
            <Check size={22} strokeWidth={1.5} />
          </div>
          <div className="overline mb-4">{isGroup ? "Group Booking Created" : "Request Received"}</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-navy leading-tight">
            Thank you, {confirmed.full_name.split(" ")[0]}.
          </h1>
          {isGroup ? (
            <>
              <p className="text-[color:var(--am-muted)] max-w-xl mx-auto mt-6 leading-relaxed">
                Your group charter for <strong className="text-navy">{confirmed.experience}</strong> is set up. Share the link below — friends pay their share, and the date locks once everyone's in.
              </p>
              <div className="mt-10 bg-pink-soft p-6 max-w-xl mx-auto">
                <div className="overline mb-3">Your Share Link</div>
                <div className="flex items-center gap-2 bg-white p-3 border border-[color:var(--am-line)]">
                  <input
                    readOnly
                    value={shareUrl}
                    className="flex-1 text-sm text-navy bg-transparent outline-none"
                    data-testid="confirmed-share-url"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shareUrl);
                      toast.success("Link copied — text it to your group.");
                    }}
                    className="text-xs tracking-[0.22em] uppercase text-navy hover:opacity-70"
                    data-testid="confirmed-copy-link"
                  >
                    Copy
                  </button>
                </div>
                <div className="text-xs text-navy/70 mt-3">
                  Total ${confirmed.estimated_total.toLocaleString()} · {confirmed.group_shares?.length || 0} shares
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
                <Link to={`/share/${confirmed.id}`} className="btn-primary" data-testid="confirmed-open-share">
                  Open Group Page <ArrowRight size={14} />
                </Link>
                <button className="btn-secondary" onClick={() => navigate("/")} data-testid="booking-home-button">
                  Back to Home
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[color:var(--am-muted)] max-w-xl mx-auto mt-6 leading-relaxed">
                Your charter request for <strong className="text-navy">{confirmed.experience}</strong> has been logged.
                We'll email you bank wire instructions within 24 hours so you can secure your date with the 50% deposit.
              </p>
              <div className="mt-10 bg-white border border-[color:var(--am-line)] p-8 text-left max-w-xl mx-auto">
                <div className="flex justify-between py-2 border-b border-[color:var(--am-line)]">
                  <span className="overline">Booking ID</span>
                  <span className="text-sm text-navy">{confirmed.id.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[color:var(--am-line)]">
                  <span className="overline">Guests</span>
                  <span className="text-sm text-navy">{confirmed.guests}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[color:var(--am-line)]">
                  <span className="overline">Dates</span>
                  <span className="text-sm text-navy">{confirmed.dates.length}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[color:var(--am-line)]">
                  <span className="overline">Estimated Total</span>
                  <span className="font-serif-display text-2xl text-navy">${confirmed.estimated_total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="overline">50% Deposit</span>
                  <span className="font-serif-display text-2xl text-navy">${(confirmed.deposit_amount || confirmed.estimated_total / 2).toLocaleString()}</span>
                </div>
              </div>
              <div className="bg-pink-soft p-6 max-w-xl mx-auto mt-6 text-left" data-testid="bank-wire-info">
                <div className="overline mb-2">Bank Wire Instructions · 50% Deposit</div>
                <div className="mb-4 text-sm text-navy">
                  <div className="font-serif-display text-2xl text-navy mb-3">
                    ${(confirmed.deposit_amount || confirmed.estimated_total / 2).toLocaleString()} USD
                  </div>
                  {bankDetails && (
                    <dl className="space-y-1.5 text-sm">
                      <div className="flex justify-between gap-3"><dt className="text-navy/60 uppercase tracking-wider text-[11px]">Bank</dt><dd className="text-navy" data-testid="bank-name">{bankDetails.bank_name}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-navy/60 uppercase tracking-wider text-[11px]">Account Name</dt><dd className="text-navy" data-testid="bank-account-name">{bankDetails.account_name}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-navy/60 uppercase tracking-wider text-[11px]">Account #</dt><dd className="text-navy font-mono" data-testid="bank-account-number">{bankDetails.account_number}</dd></div>
                      <div className="flex justify-between gap-3"><dt className="text-navy/60 uppercase tracking-wider text-[11px]">Address</dt><dd className="text-navy text-right">{bankDetails.address}</dd></div>
                      <div className="flex justify-between gap-3 pt-2 border-t border-navy/20 mt-2"><dt className="text-navy/60 uppercase tracking-wider text-[11px]">Reference</dt><dd className="text-navy font-mono">Booking {confirmed.id.slice(0, 8)}</dd></div>
                    </dl>
                  )}
                </div>
                <p className="text-xs text-navy/70 leading-relaxed">
                  Include the booking reference so we can match your wire. A copy of these details has been emailed to <strong>{confirmed.email}</strong>. Once funds land, your charter is locked in.
                </p>
                {bankDetails && (
                  <button
                    type="button"
                    onClick={() => {
                      const deposit = (confirmed.deposit_amount || confirmed.estimated_total / 2).toLocaleString();
                      const text = [
                        `Anchor Management — 50% Deposit Wire Instructions`,
                        ``,
                        `Amount: $${deposit} USD`,
                        `Bank: ${bankDetails.bank_name}`,
                        `Account Name: ${bankDetails.account_name}`,
                        `Account Number: ${bankDetails.account_number}`,
                        `Address: ${bankDetails.address}`,
                        `Reference: Booking ${confirmed.id.slice(0, 8)}`,
                      ].join("\n");
                      navigator.clipboard
                        .writeText(text)
                        .then(() => toast.success("Wire details copied to clipboard"))
                        .catch(() => toast.error("Could not copy — please select and copy manually"));
                    }}
                    className="mt-4 w-full flex items-center justify-center gap-2 bg-navy text-white py-3 text-xs tracking-[0.22em] uppercase hover:bg-navy/90 transition-colors"
                    data-testid="copy-wire-details"
                  >
                    <Copy size={14} /> Copy Wire Details
                  </button>
                )}

                <label
                  className={`mt-3 flex items-center gap-3 bg-white border border-[color:var(--am-line)] p-4 cursor-pointer hover:border-navy transition-colors ${
                    wireSent ? "opacity-60 cursor-default" : ""
                  }`}
                  data-testid="wire-sent-label"
                >
                  <input
                    type="checkbox"
                    checked={wireSent}
                    disabled={wireSent || wireSentSubmitting}
                    onChange={async () => {
                      if (wireSent) return;
                      setWireSentSubmitting(true);
                      try {
                        await axios.post(`${API}/bookings/${confirmed.id}/wire-sent`);
                        setWireSent(true);
                        toast.success("Thanks — we'll watch for the wire and lock your date in once it lands.");
                      } catch (e) {
                        toast.error("Could not mark wire as sent. Please try again.");
                      } finally {
                        setWireSentSubmitting(false);
                      }
                    }}
                    className="w-4 h-4 accent-[color:var(--am-navy)]"
                    data-testid="wire-sent-checkbox"
                  />
                  <span className="text-sm text-navy">
                    {wireSent ? "Wire marked as sent — we'll confirm by email." : "I've sent the wire"}
                  </span>
                </label>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-10">
                <button className="btn-primary" onClick={() => navigate("/")} data-testid="booking-home-button">
                  Back to Home
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-sand pb-28 lg:pb-0">
      <section className="pt-28 pb-10 max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="overline mb-5">Book Now</div>
        <h1 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl text-navy leading-[1.02] max-w-3xl">
          Design your day on the water.
        </h1>
      </section>

      {/* Step Indicator */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-10">
        <div className="flex items-center justify-between md:justify-start md:gap-10 overflow-x-auto border-b border-[color:var(--am-line)] pb-5">
          {STEPS.map((s, idx) => {
            const active = step === s.id;
            const done = step > s.id;
            return (
              <button
                key={s.id}
                onClick={() => s.id < step && setStep(s.id)}
                className={`flex items-center gap-3 text-xs tracking-[0.22em] uppercase font-medium whitespace-nowrap ${
                  active ? "text-navy" : done ? "text-navy/60" : "text-[color:var(--am-muted)]"
                }`}
                data-testid={`step-${s.id}`}
              >
                <span
                  className={`w-7 h-7 flex items-center justify-center border ${
                    active
                      ? "border-navy bg-navy text-white"
                      : done
                      ? "border-navy text-navy"
                      : "border-[color:var(--am-line)] text-[color:var(--am-muted)]"
                  }`}
                >
                  {done ? <Check size={12} /> : <s.icon size={12} />}
                </span>
                <span className="hidden md:inline">{s.label}</span>
                {idx < STEPS.length - 1 && (
                  <span className="hidden md:block w-10 h-px bg-[color:var(--am-line)] ml-6" />
                )}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          {/* STEP 1 */}
          {step === 1 && (
            <div className="fade-up" data-testid="step-1-content">
              <h2 className="font-serif-display text-3xl text-navy mb-8">Choose your experience</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {EXPERIENCES.map((e) => (
                  <button
                    key={e.slug}
                    onClick={() => setExperience(e.name)}
                    className={`text-left border p-6 transition-all ${
                      experience === e.name
                        ? "border-navy bg-white"
                        : "border-[color:var(--am-line)] bg-white hover:border-navy/50"
                    }`}
                    data-testid={`exp-option-${e.slug}`}
                  >
                    <div className="overline mb-2">{e.pricing}</div>
                    <div className="font-serif-display text-2xl text-navy">{e.name}</div>
                    <div className="text-sm text-[color:var(--am-muted)] mt-2 italic">{e.tagline}</div>
                  </button>
                ))}
              </div>

              <div className="mt-10 max-w-sm">
                <Label htmlFor="guests" className="overline block mb-3">
                  {isBusTour ? "Adults" : "Number of Guests"}
                </Label>
                <div className="flex items-center gap-3" data-testid="guests-stepper">
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.max(1, g - 1))}
                    disabled={guests <= 1}
                    className="w-12 h-12 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Decrease guests"
                    data-testid="guests-decrement"
                  >
                    <Minus size={16} strokeWidth={1.6} />
                  </button>
                  <div
                    className="flex-1 text-center font-serif-display text-4xl text-navy py-2 border-b-2 border-navy/30 select-none"
                    data-testid="guests-value"
                  >
                    {guests}
                  </div>
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.min(maxGuests, g + 1))}
                    disabled={guests >= maxGuests}
                    className="w-12 h-12 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                    aria-label="Increase guests"
                    data-testid="guests-increment"
                  >
                    <Plus size={16} strokeWidth={1.6} />
                  </button>
                </div>
                <p className="text-xs text-[color:var(--am-muted)] mt-3">
                  {isBusTour
                    ? "Up to 20 guests total. The bus is flat $75 per hour."
                    : "Up to 10 guests. Base rate covers 8; extras (9–10) are $50/hr each."}
                </p>
              </div>

              {isBusTour && (
                <div className="mt-8 max-w-sm" data-testid="children-section">
                  <Label className="overline block mb-3">Children</Label>
                  <div className="flex items-center gap-3" data-testid="children-stepper">
                    <button
                      type="button"
                      onClick={() => setChildren((c) => Math.max(0, c - 1))}
                      disabled={children <= 0}
                      className="w-12 h-12 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                      aria-label="Decrease children"
                      data-testid="children-decrement"
                    >
                      <Minus size={16} strokeWidth={1.6} />
                    </button>
                    <div
                      className="flex-1 text-center font-serif-display text-4xl text-navy py-2 border-b-2 border-navy/30 select-none"
                      data-testid="children-value"
                    >
                      {children}
                    </div>
                    <button
                      type="button"
                      onClick={() => setChildren((c) => Math.min(maxGuests, c + 1))}
                      disabled={children >= maxGuests}
                      className="w-12 h-12 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                      aria-label="Increase children"
                      data-testid="children-increment"
                    >
                      <Plus size={16} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="fade-up" data-testid="step-2-content">
              <h2 className="font-serif-display text-3xl text-navy mb-2">Pick your date(s) & time</h2>
              <p className="text-[color:var(--am-muted)] mb-8">
                Select a date, choose your time and duration, then add it. You can stack multiple dates.
              </p>

              <div className="grid md:grid-cols-2 gap-8 bg-white border border-[color:var(--am-line)] p-6">
                <div>
                  <Calendar
                    mode="single"
                    selected={currentDate}
                    onSelect={setCurrentDate}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    data-testid="booking-calendar"
                  />
                </div>
                <div className="space-y-5">
                  {!isOvernight && (
                    <>
                      <div>
                        <Label className="overline block mb-3">Start Time</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {TIME_SLOTS.map((t) => (
                            <button
                              key={t}
                              onClick={() => setStartTime(t)}
                              className={`py-2 text-sm border transition-colors ${
                                startTime === t
                                  ? "border-navy bg-navy text-white"
                                  : "border-[color:var(--am-line)] text-navy hover:border-navy"
                              }`}
                              data-testid={`time-${t}`}
                            >
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="overline block mb-3">Duration (hours)</Label>
                        <div className="flex items-center gap-3">
                          <button
                            className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                            onClick={() => setDurationHours((v) => Math.max(minDurationHours, v - 1))}
                            disabled={durationHours <= minDurationHours}
                            data-testid="duration-minus"
                          >−</button>
                          <div className="font-serif-display text-3xl text-navy w-10 text-center">{durationHours}</div>
                          <button
                            className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white"
                            onClick={() => setDurationHours((v) => Math.min(12, v + 1))}
                            data-testid="duration-plus"
                          >+</button>
                        </div>
                        {isBusTour && (
                          <p className="text-xs text-[color:var(--am-muted)] mt-2">
                            $100 for first 4 hours · +$25 per additional hour
                          </p>
                        )}
                      </div>
                    </>
                  )}
                  {isOvernight && (
                    <div className="bg-pink-soft p-5">
                      <div className="overline mb-2">Overnight</div>
                      <p className="text-sm text-navy/80 leading-relaxed">
                        Boarding from 5pm, disembark by 10am the following morning. Flat rate $1,500 / night.
                      </p>
                    </div>
                  )}

                  <button
                    className="btn-primary w-full justify-center"
                    onClick={addDate}
                    data-testid="add-date-button"
                  >
                    <Plus size={14} /> Add Date to Charter
                  </button>
                </div>
              </div>

              {dates.length > 0 && (
                <div className="mt-8">
                  <div className="overline mb-4">Dates in your charter</div>
                  <ul className="space-y-3">
                    {dates.map((d) => (
                      <li
                        key={toISO(d.date)}
                        className="bg-white border border-[color:var(--am-line)] p-4 flex items-center justify-between"
                        data-testid={`booking-date-${toISO(d.date)}`}
                      >
                        <div>
                          <div className="font-serif-display text-xl text-navy">{formatDate(d.date)}</div>
                          <div className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)] mt-1 flex gap-4">
                            {d.overnight ? (
                              <span>Overnight</span>
                            ) : (
                              <>
                                <span className="flex items-center gap-1"><Clock size={12} /> {d.start_time}</span>
                                <span>· {d.duration_hours}h</span>
                              </>
                            )}
                            <span className="flex items-center gap-1"><Users size={12} /> {guests}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeDate(toISO(d.date))}
                          className="text-[color:var(--am-muted)] hover:text-navy"
                          data-testid={`remove-date-${toISO(d.date)}`}
                          aria-label="remove date"
                        >
                          <Trash2 size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="fade-up" data-testid="step-3-content">
              <h2 className="font-serif-display text-3xl text-navy mb-2">Add something to the day</h2>
              <p className="text-[color:var(--am-muted)] mb-8">
                Optional food, drink and experience packages. Tap any item to add it.
              </p>
              {["Meals", "Platters", "Bar"].map((cat) => (
                <div key={cat} className="mb-8" data-testid={`addon-category-${cat.toLowerCase()}`}>
                  <div className="overline mb-3">{cat === "Bar" ? "Bar Packages" : cat}</div>
                  <div className="space-y-3">
                    {ADD_ONS.filter((a) => a.category === cat).map((a) => {
                      const checked = addOns.includes(a.id);
                      const qty = addOnQty[a.id] || 0;
                      const qtyLabel = a.unit === "flat" ? "Qty" : "For how many guests?";
                      return (
                        <div
                          key={a.id}
                          className={`border transition-colors ${
                            checked ? "border-navy bg-white" : "border-[color:var(--am-line)] bg-white hover:border-navy/50"
                          }`}
                          data-testid={`addon-${a.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleAddon(a.id)}
                            className="w-full text-left flex items-center gap-4 p-5 cursor-pointer"
                            data-testid={`addon-toggle-${a.id}`}
                          >
                            <span
                              className={`w-5 h-5 flex-shrink-0 border flex items-center justify-center ${
                                checked ? "bg-navy border-navy" : "border-[color:var(--am-muted)]"
                              }`}
                              aria-hidden="true"
                            >
                              {checked && <Check size={13} strokeWidth={2.5} className="text-white" />}
                            </span>
                            <div className="flex-1">
                              <div className="font-serif-display text-xl text-navy">{a.label}</div>
                              <div className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)] mt-1">
                                {a.unit === "flat" ? "flat fee" : a.unit}
                              </div>
                            </div>
                            <div className="font-serif-display text-xl text-navy">${a.price}</div>
                          </button>
                          {checked && (
                            <div
                              className="flex items-center justify-between gap-4 px-5 py-3 border-t border-[color:var(--am-line)] bg-sand"
                              data-testid={`addon-qty-row-${a.id}`}
                            >
                              <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[color:var(--am-muted)]">
                                {qtyLabel}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); changeAddonQty(a.id, -1); }}
                                  disabled={qty <= 1}
                                  className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white disabled:opacity-30 transition-colors flex items-center justify-center"
                                  data-testid={`addon-qty-dec-${a.id}`}
                                  aria-label="Decrease quantity"
                                >
                                  <Minus size={14} strokeWidth={1.6} />
                                </button>
                                <div className="font-serif-display text-xl text-navy w-10 text-center" data-testid={`addon-qty-${a.id}`}>
                                  {qty}
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); changeAddonQty(a.id, 1); }}
                                  className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white transition-colors flex items-center justify-center"
                                  data-testid={`addon-qty-inc-${a.id}`}
                                  aria-label="Increase quantity"
                                >
                                  <Plus size={14} strokeWidth={1.6} />
                                </button>
                                <div className="text-xs text-navy/60 ml-2 hidden sm:block">
                                  = ${(a.price * qty).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="fade-up" data-testid="step-4-content">
              <h2 className="font-serif-display text-3xl text-navy mb-2">Your details</h2>
              <p className="text-[color:var(--am-muted)] mb-8">
                We'll send confirmation and deposit instructions to this email.
              </p>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="overline block mb-2">Full Name</Label>
                  <Input id="name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rounded-none" data-testid="form-name" />
                </div>
                <div>
                  <Label htmlFor="email" className="overline block mb-2">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" data-testid="form-email" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone" className="overline block mb-2">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" data-testid="form-phone" />
                </div>
                <div className="md:col-span-2">
                  <Label className="overline block mb-3">
                    {isBusTour ? "Pickup Location" : "Pickup Dock"}
                  </Label>
                  {isBusTour ? (
                    <Input
                      value={form.pickup_dock}
                      onChange={(e) => setForm({ ...form, pickup_dock: e.target.value })}
                      placeholder="Hotel name, address, or any meeting point"
                      className="rounded-none"
                      data-testid="pickup-location-input"
                    />
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pickup-dock-options">
                      {PICKUP_DOCKS.map((d) => {
                        const selected = form.pickup_dock === d;
                        return (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setForm({ ...form, pickup_dock: d })}
                            className={`px-4 py-3 text-sm border transition-colors ${
                              selected
                                ? "bg-navy text-white border-navy"
                                : "bg-white text-navy border-[color:var(--am-line)] hover:border-navy"
                            }`}
                            data-testid={`pickup-dock-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          >
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-[color:var(--am-muted)] mt-2">
                    {isBusTour ? "Where shall we collect you?" : "Where would you like us to meet you?"}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="notes" className="overline block mb-2">
                    {isBusTour ? "Requested Tour Stops or Notes" : "Anything we should know?"}
                    <span className="ml-2 text-[color:var(--am-muted)] normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Textarea
                    id="notes"
                    rows={4}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder={isBusTour ? "e.g. Horseshoe Bay, Crystal Caves, St. George's, lunch stop at Wahoo's…" : "Allergies, special requests, occasion…"}
                    className="rounded-none"
                    data-testid="form-notes"
                  />
                </div>
                <div className="md:col-span-2 pt-4 border-t border-[color:var(--am-line)]">
                  <Label htmlFor="voucher" className="overline block mb-2">Gift Voucher Code (optional)</Label>
                  {voucher ? (
                    <div className="bg-pink-soft p-4 flex items-center justify-between" data-testid="voucher-applied">
                      <div>
                        <div className="font-mono text-sm text-navy tracking-wider">{voucher.code}</div>
                        <div className="text-xs text-[color:var(--am-muted)] mt-1">
                          Balance ${voucher.balance.toLocaleString()} · applied to your charter
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearVoucher}
                        className="text-xs tracking-[0.2em] uppercase text-navy underline"
                        data-testid="voucher-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        id="voucher"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                        placeholder="ANC-XXXX-XXXX"
                        className="rounded-none font-mono tracking-wider"
                        data-testid="voucher-input"
                      />
                      <button
                        type="button"
                        onClick={applyVoucher}
                        disabled={voucherChecking || !voucherCode}
                        className="btn-secondary py-2 px-4 text-[0.72rem] whitespace-nowrap"
                        data-testid="voucher-apply"
                      >
                        {voucherChecking ? "Checking…" : "Apply"}
                      </button>
                    </div>
                  )}
                </div>

                {/* Share a Sail — temporarily disabled; bank-wire-only flow */}
                {false && (
                <div className="md:col-span-2 pt-6 border-t border-[color:var(--am-line)]">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="group-toggle-label">
                    <input
                      type="checkbox"
                      checked={groupEnabled}
                      onChange={(e) => setGroupEnabled(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[color:var(--am-navy)]"
                      data-testid="group-toggle"
                    />
                    <span>
                      <span className="font-serif-display text-xl text-navy">Split this charter with friends</span>
                      <span className="block text-xs text-[color:var(--am-muted)] mt-1">
                        Pay your share, send a link to friends, they pay theirs. Charter locks at 100%.
                      </span>
                    </span>
                  </label>

                  {groupEnabled && quote && (
                    <div className="mt-6 bg-pink-soft p-6 space-y-5" data-testid="group-config">
                      <div>
                        <div className="overline mb-3">Split Mode</div>
                        <div className="flex border border-[color:var(--am-line)] bg-white">
                          <button
                            type="button"
                            onClick={() => setSplitMode("equal")}
                            className={`flex-1 py-2 text-xs tracking-[0.22em] uppercase ${
                              splitMode === "equal" ? "bg-navy text-white" : "text-navy"
                            }`}
                            data-testid="group-mode-equal"
                          >
                            Equal Split
                          </button>
                          <button
                            type="button"
                            onClick={() => setSplitMode("custom")}
                            className={`flex-1 py-2 text-xs tracking-[0.22em] uppercase ${
                              splitMode === "custom" ? "bg-navy text-white" : "text-navy"
                            }`}
                            data-testid="group-mode-custom"
                          >
                            Custom Shares
                          </button>
                        </div>
                      </div>

                      {splitMode === "equal" ? (
                        <div>
                          <Label className="overline block mb-3">Number of People (including you)</Label>
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white"
                              onClick={() => setPartySize((v) => Math.max(2, v - 1))}
                              data-testid="group-party-minus"
                            >
                              −
                            </button>
                            <div className="font-serif-display text-3xl text-navy w-10 text-center" data-testid="group-party-size">
                              {partySize}
                            </div>
                            <button
                              type="button"
                              className="w-9 h-9 border border-navy text-navy hover:bg-navy hover:text-white"
                              onClick={() => setPartySize((v) => Math.min(20, v + 1))}
                              data-testid="group-party-plus"
                            >
                              +
                            </button>
                            <div className="ml-3 text-sm text-navy/80">
                              ≈ <span className="font-serif-display text-xl">${(((quote.adjusted_total ?? quote.estimated_total) / partySize) || 0).toFixed(0)}</span> each
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <Label className="overline">Custom Shares</Label>
                            <div className="text-xs text-navy/70">
                              Total ${(quote.adjusted_total ?? quote.estimated_total).toLocaleString()}
                            </div>
                          </div>
                          <div className="space-y-2">
                            {customShares.map((s, i) => (
                              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`group-share-${i}`}>
                                <Input
                                  placeholder={i === 0 ? "You" : "Friend's name"}
                                  value={s.name}
                                  onChange={(e) => {
                                    const next = [...customShares];
                                    next[i] = { ...next[i], name: e.target.value };
                                    setCustomShares(next);
                                  }}
                                  className="col-span-5 rounded-none bg-white"
                                />
                                <Input
                                  placeholder="email"
                                  type="email"
                                  value={s.email}
                                  onChange={(e) => {
                                    const next = [...customShares];
                                    next[i] = { ...next[i], email: e.target.value };
                                    setCustomShares(next);
                                  }}
                                  className="col-span-4 rounded-none bg-white"
                                />
                                <Input
                                  type="number"
                                  placeholder="$"
                                  value={s.amount || ""}
                                  onChange={(e) => {
                                    const next = [...customShares];
                                    next[i] = { ...next[i], amount: Number(e.target.value) };
                                    setCustomShares(next);
                                  }}
                                  className="col-span-2 rounded-none bg-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => setCustomShares(customShares.filter((_, j) => j !== i))}
                                  disabled={customShares.length <= 2}
                                  className="col-span-1 text-navy/60 hover:text-navy disabled:opacity-30"
                                  aria-label="remove share"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setCustomShares([...customShares, { name: "", email: "", amount: 0 }])}
                            className="mt-3 text-xs tracking-[0.22em] uppercase text-navy underline"
                            data-testid="group-add-share"
                          >
                            + Add another person
                          </button>
                          <div className="mt-3 text-xs text-navy/70">
                            Sum: ${customShares.reduce((acc, s) => acc + (Number(s.amount) || 0), 0).toLocaleString()} · Must equal total above
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Prominent estimated total card just above Submit */}
              <div className="mt-10 border-2 border-navy bg-pink-soft p-6 sm:p-8" data-testid="step4-total-card">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="overline mb-2">Estimated Total</div>
                    <div className="font-serif-display text-4xl sm:text-5xl text-navy leading-none" data-testid="step4-total-amount">
                      {quote !== null ? `$${(quote.adjusted_total ?? quote.estimated_total ?? 0).toLocaleString()}` : "—"}
                    </div>
                    {quote && quote.voucher_used > 0 && (
                      <div className="text-sm text-navy/70 mt-2" data-testid="step4-voucher-applied">
                        Voucher applied: −${quote.voucher_used.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--am-muted)] max-w-xs leading-relaxed sm:text-right">
                    Estimate only. 50% deposit invoice will be emailed once the captain confirms your date.
                  </div>
                </div>
                {quote && (
                  <div className="mt-5 pt-5 border-t border-navy/20 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm" data-testid="step4-breakdown">
                    <div className="flex justify-between">
                      <span className="text-[color:var(--am-muted)]">{experience}</span>
                      <span className="text-navy">${(quote.charter_total ?? 0).toLocaleString()}</span>
                    </div>
                    {addOns.length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-[color:var(--am-muted)]">Add-ons ({addOns.length})</span>
                        <span className="text-navy">${(quote.addon_total ?? 0).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mt-12">
            <button
              className="btn-secondary"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              data-testid="step-back"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < 4 ? (
              <button
                className="btn-primary"
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                data-testid="step-next"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={submit}
                disabled={!canNext || submitting}
                data-testid="submit-booking"
              >
                {submitting ? "Submitting..." : "Submit Request"} <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>

        {/* SUMMARY */}
        <aside className="lg:col-span-4">
          <div className="sticky top-28 bg-white border border-[color:var(--am-line)] p-8" data-testid="booking-summary">
            <div className="overline mb-4">Your Charter</div>
            <div className="space-y-4">
              <div className="flex justify-between pb-3 border-b border-[color:var(--am-line)]">
                <span className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)]">Experience</span>
                <span className="text-sm text-navy text-right max-w-[55%]">{experience}</span>
              </div>
              <div className="flex justify-between pb-3 border-b border-[color:var(--am-line)]">
                <span className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)]">Guests</span>
                <span className="text-sm text-navy">{guests}</span>
              </div>
              <div className="flex justify-between pb-3 border-b border-[color:var(--am-line)]">
                <span className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)]">Dates</span>
                <span className="text-sm text-navy">{dates.length || "—"}</span>
              </div>
              <div className="flex justify-between pb-3 border-b border-[color:var(--am-line)]">
                <span className="text-xs tracking-[0.18em] uppercase text-[color:var(--am-muted)]">Add-ons</span>
                <span className="text-sm text-navy">{addOns.length || "—"}</span>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-navy/20">
              {quote && quote.voucher_used > 0 && (
                <div className="flex justify-between mb-3 text-sm">
                  <span className="text-[color:var(--am-muted)]">Voucher applied</span>
                  <span className="text-navy">−${quote.voucher_used.toLocaleString()}</span>
                </div>
              )}
              <div className="overline mb-2">Estimated Total</div>
              <div className="font-serif-display text-4xl text-navy" data-testid="booking-total">
                {quote !== null ? `$${(quote.adjusted_total ?? quote.estimated_total ?? 0).toLocaleString()}` : "—"}
              </div>
              <p className="text-xs text-[color:var(--am-muted)] mt-2 leading-relaxed">
                Estimate only. Final quote and 50% deposit invoice sent after confirmation.
              </p>
            </div>
          </div>
        </aside>
      </section>

      {/* Sticky mobile bottom bar — shows total + primary action */}
      {!confirmed && (
        <div
          className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-navy/20 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-4 py-3 flex items-center justify-between gap-3"
          data-testid="booking-sticky-bar"
        >
          <div className="leading-tight">
            <div className="text-[0.65rem] tracking-[0.18em] uppercase text-[color:var(--am-muted)]">Est. Total</div>
            <div className="font-serif-display text-2xl text-navy" data-testid="sticky-total">
              {quote !== null ? `$${(quote.adjusted_total ?? quote.estimated_total ?? 0).toLocaleString()}` : "—"}
            </div>
          </div>
          {step < 4 ? (
            <button
              className="btn-primary py-3 px-5 text-xs whitespace-nowrap"
              onClick={() => canNext && setStep((s) => s + 1)}
              disabled={!canNext}
              data-testid="sticky-next"
            >
              Continue
            </button>
          ) : (
            <button
              className="btn-primary py-3 px-5 text-xs whitespace-nowrap"
              onClick={submit}
              disabled={!canNext || submitting}
              data-testid="sticky-submit"
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
